import {z} from 'zod';

export const InvoiceProduct=z.enum(['billing','payments','crypto','domains']);
export const InvoiceFilter=z.object({
  product:InvoiceProduct.optional(),accountId:z.uuid().optional(),
  query:z.string().trim().max(150).optional(),mode:z.enum(['live','test']).optional(),
  from:z.iso.date().optional(),to:z.iso.date().optional(),
  beforeDate:z.iso.datetime().optional(),beforeId:z.string().max(150).optional(),
}).strict().refine(value=>Boolean(value.beforeDate)===Boolean(value.beforeId),'Both invoice cursor fields are required.')
  .refine(value=>!value.from||!value.to||value.from<=value.to,'The start date must not follow the end date.');
export type InvoiceFilterInput=z.infer<typeof InvoiceFilter>;
export const InvoiceItem=z.object({id:z.string(),accountId:z.uuid(),profileName:z.string(),
  product:InvoiceProduct,description:z.string(),number:z.string().nullable(),status:z.string(),
  createdAt:z.iso.datetime(),currency:z.string(),amount:z.string(),decimals:z.number().int(),
  mode:z.enum(['live','test']),direction:z.enum(['received','issued']),downloadable:z.boolean(),
  sourceId:z.string(),error:z.unknown().nullable(),
  creditQuantity:z.string().regex(/^\d+(?:\.\d+)?$/).nullable(),
}).strict();
export const InvoicePage=z.object({items:InvoiceItem.array(),next:z.object({beforeDate:z.iso.datetime(),beforeId:z.string()}).nullable(),filter:InvoiceFilter}).strict();
export type InvoicePageData=z.infer<typeof InvoicePage>;

const text=z.string().trim().min(1).max(500),minor=z.string().regex(/^\d{1,38}$/);
export const InvoiceParty=z.object({name:text,address:z.array(text).min(1).max(8),email:z.email().nullable(),registrationNumber:text.nullable(),taxId:text.nullable()}).strict();
export const InvoiceContent=z.object({
  seller:InvoiceParty,buyer:InvoiceParty,currency:z.string().regex(/^[A-Z]{3}$/),decimals:z.number().int().min(0).max(3),
  suppliedAt:z.iso.datetime(),paymentMethod:text,paymentReference:text,
  lines:z.array(z.object({description:text,quantity:z.string().regex(/^[1-9]\d{0,8}$/),
    unitAmount:minor,subtotal:minor,tax:minor,total:minor,periodStart:z.iso.date().nullable(),periodEnd:z.iso.date().nullable(),
  }).strict()).min(1).max(100),
  subtotal:minor,tax:minor,total:minor,
  taxes:z.array(z.object({label:text,rate:z.string().regex(/^\d+(?:\.\d+)?$/).nullable(),taxable:minor,amount:minor}).strict()).max(30),
  taxReporting:z.object({currency:z.string().regex(/^[A-Z]{3}$/),amount:minor,decimals:z.number().int().min(0).max(3),rate:text,evidence:text}).strict().nullable(),
  notes:z.array(text).max(20),
}).strict().superRefine((value,ctx)=>{
  const fail=(message:string)=>ctx.addIssue({code:'custom',message});
  let subtotal=0n,tax=0n;
  for(const line of value.lines){
    if(BigInt(line.unitAmount)*BigInt(line.quantity)!==BigInt(line.subtotal)||BigInt(line.subtotal)+BigInt(line.tax)!==BigInt(line.total))fail('Invoice line amounts do not agree.');
    if(Boolean(line.periodStart)!==Boolean(line.periodEnd)||line.periodStart&&line.periodEnd&&line.periodStart>line.periodEnd)fail('Invoice service dates do not agree.');
    subtotal+=BigInt(line.subtotal);tax+=BigInt(line.tax);
  }
  if(subtotal!==BigInt(value.subtotal)||tax!==BigInt(value.tax)||subtotal+tax!==BigInt(value.total))fail('Invoice totals do not agree with its lines.');
  if(value.taxes.reduce((sum,item)=>sum+BigInt(item.amount),0n)!==tax)fail('Invoice tax details do not agree with the tax total.');
});
export type InvoiceContentData=z.infer<typeof InvoiceContent>;
export function invoiceAmount(amount:string,decimals:number,currency:string){
  const negative=amount.startsWith('-'),digits=(negative?amount.slice(1):amount).padStart(decimals+1,'0');
  const whole=(decimals?digits.slice(0,-decimals):digits).replace(/\B(?=(\d{3})+(?!\d))/g,',');
  return `${currency} ${negative?'-':''}${whole}${decimals?`.${digits.slice(-decimals)}`:''}`;
}
